import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ImageIcon, XIcon } from 'lucide-react';
import { readImages } from '../../utils/files';
import type { StoredImage } from '../../utils/files';
import { Button } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseCategories, caseStatuses, priceModels } from '../../types/admin';
import type { CaseCategory, CaseStatus, PriceModel } from '../../types/admin';
const NEW_CUSTOMER = 'ny';
export function CaseNew() {
  const {
    customers,
    createCase,
    settings
  } = useAdminData();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(customers[0]?.id ?? NEW_CUSTOMER);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<CaseCategory>('Multiservice');
  const [priceModel, setPriceModel] = useState<PriceModel>('Tilbud endnu ikke lavet');
  const [hourlyRate, setHourlyRate] = useState(settings.hourlyRate);
  const [fixedPrice, setFixedPrice] = useState('');
  const [plannedDate, setPlannedDate] = useState('');
  const [status, setStatus] = useState<CaseStatus>('Ny henvendelse');
  const [internalNote, setInternalNote] = useState('');
  const [photos, setPhotos] = useState<StoredImage[]>([]);
  const creatingCustomer = customerId === NEW_CUSTOMER;
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-325" data-mp="hatqxu60tnib">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-326" data-mp="mb81biv3h9ji">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-327" data-mp="db1rq82iotpm" />
        Sager
      </Link>
      <h1 className="mt-4 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-328" data-mp="2f0h0fmosyaeo">
        Opret sag
      </h1>

      <form className="mt-7 max-w-3xl space-y-5" onSubmit={event => {
      event.preventDefault();
      const created = createCase({
        customerId: creatingCustomer ? '' : customerId,
        newCustomer: creatingCustomer ? {
          name,
          phone,
          email,
          address
        } : undefined,
        title,
        address,
        description,
        category,
        priceModel,
        hourlyRate: priceModel === 'Timepris' ? hourlyRate : '',
        fixedPrice: priceModel === 'Fast pris' ? fixedPrice : '',
        plannedDate,
        status,
        internalNote,
        photos
      });
      navigate(`/admin/sager/${created.id}`);
    }} data-id="element-329" data-mp="2a8z2l7i2spxx">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-330" data-mp="1dhf0nqrckej2">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-331" data-mp="18ityvmwg5msi">Kunde</h2>
          <SelectField id="ns-kunde" label="Vælg kunde" className="mt-5" value={customerId} onChange={event => setCustomerId(event.target.value)} options={[...customers.map(customer => ({
          value: customer.id,
          label: customer.name
        })), {
          value: NEW_CUSTOMER,
          label: '+ Opret ny kunde'
        }]} data-id="element-332" data-mp="3k91uakgfwlml" />
          {creatingCustomer && <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-333" data-mp="brosg0lli8ww">
              <TextField id="ns-navn" label="Navn" required value={name} onChange={event => setName(event.target.value)} data-id="element-334" data-mp="bq408hmr7227" />
              <TextField id="ns-tlf" label="Telefonnummer" type="tel" value={phone} onChange={event => setPhone(event.target.value)} data-id="element-335" data-mp="kqaj6j180u8r" />
              <TextField id="ns-mail" label="E-mail" type="email" className="sm:col-span-2" value={email} onChange={event => setEmail(event.target.value)} data-id="element-336" data-mp="1r9a8q78yp654" />
            </div>}
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-337" data-mp="1w9y04u13rozv">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-338" data-mp="18islgi6wf2r5">Opgave</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-339" data-mp="13kioc21m5s2w">
            <TextField id="ns-titel" label="Opgavetitel" required placeholder="Fx montering af hegn" value={title} onChange={event => setTitle(event.target.value)} className="sm:col-span-2" data-id="element-340" data-mp="13m24ztr6m557" />
            <TextField id="ns-adresse" label="Adresse" placeholder="Vej, nummer, postnr. og by" value={address} onChange={event => setAddress(event.target.value)} className="sm:col-span-2" data-id="element-341" data-mp="pqzpcewj91f7" />
            <SelectField id="ns-kategori" label="Kategori" value={category} onChange={event => setCategory(event.target.value as CaseCategory)} options={caseCategories.map(option => ({
            value: option,
            label: option
          }))} data-id="element-342" data-mp="319e6ivxtz3es" />
            <TextField id="ns-dato" label="Planlagt dato" type="date" value={plannedDate} onChange={event => setPlannedDate(event.target.value)} data-id="element-343" data-mp="3f8dppo2kqq3k" />
          </div>
          <TextArea id="ns-beskrivelse" label="Opgavebeskrivelse" className="mt-5" rows={4} value={description} onChange={event => setDescription(event.target.value)} data-id="element-344" data-mp="3b5hfakn6u0ap" />
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-345" data-mp="1zohfz52fal76">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-346" data-mp="59u2qdhh0fa7">Pris og status</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-347" data-mp="caohdkrw2i9">
            <SelectField id="ns-prisform" label="Prisform" value={priceModel} onChange={event => setPriceModel(event.target.value as PriceModel)} options={priceModels.map(option => ({
            value: option,
            label: option
          }))} data-id="element-348" data-mp="3rpersdjjuexv" />
            <SelectField id="ns-status" label="Status" value={status} onChange={event => setStatus(event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
            value: option,
            label: option
          }))} data-id="element-349" data-mp="3dpdrp0b3zbjg" />
            {priceModel === 'Timepris' && <TextField id="ns-timepris" label="Timepris (kr.)" inputMode="numeric" value={hourlyRate} onChange={event => setHourlyRate(event.target.value)} data-id="element-350" data-mp="16xv3pjgxspgx" />}
            {priceModel === 'Fast pris' && <TextField id="ns-fastpris" label="Fast pris (kr.)" inputMode="numeric" value={fixedPrice} onChange={event => setFixedPrice(event.target.value)} data-id="element-351" data-mp="2ulsgf9hd6h7p" />}
          </div>
          <TextArea id="ns-noter" label="Interne noter" className="mt-5" rows={3} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." value={internalNote} onChange={event => setInternalNote(event.target.value)} data-id="element-352" data-mp="21z7s6hw6ql8d" />
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-353" data-mp="3mdhtes7vod45">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-354" data-mp="3rc2tmrn4qwsl">Billeder</h2>
          <label htmlFor="ns-billeder" className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-355" data-mp="3rdw2mdl7tt3r">
            <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-356" data-mp="3btnjan9dfz4w" />
            <span className="text-[15px] font-medium text-ink" data-id="element-357" data-mp="36z9lyfb5fhxl">Vælg billeder</span>
            <span className="text-sm text-ink-muted" data-id="element-358" data-mp="3ls3ep2ffluk7">Du kan tilføje flere billeder</span>
            <input id="ns-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={async event => {
            const input = event.target;
            const added = await readImages(input.files);
            setPhotos(current => [...current, ...added]);
            input.value = '';
          }} data-id="element-359" data-mp="3gqnsv85fjk0r" />
          </label>
          {photos.length > 0 && <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4" data-id="element-360" data-mp="1m7nl0ene1zuf">
              {photos.map((photo, index) => <li key={`${photo.name}-${index}`} className="relative" data-id="element-361" data-mp="1qatml8zk32uk" data-mpi={index}>
                  <img src={photo.url} alt={photo.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-362" data-mp="1cgj2x62f1yvd" />
                  <button type="button" onClick={() => setPhotos(current => current.filter((_, i) => i !== index))} aria-label={`Fjern billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-363" data-mp="12id632vgvixd">
                    <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-364" data-mp="2svkeamm6wd0v" />
                  </button>
                </li>)}
            </ul>}
        </section>

        <div className="flex flex-col gap-3 sm:flex-row" data-id="element-365" data-mp="dunfir894gwp">
          <Button type="submit" size="lg" data-id="element-366" data-mp="imylaapoawx6">
            Gem sag
          </Button>
          <Button type="button" variant="secondary" size="lg" onClick={() => navigate('/admin/sager')} data-id="element-367" data-mp="4vtqpn15wutp">
            Annuller
          </Button>
        </div>
      </form>
    </div>;
}